import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Eye, Trash2 } from 'lucide-react';
import { useState } from 'react';
import type { PlatformUserListItem } from '@bm/contracts';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Input } from '../components/ui';
import { PlatformTabs } from '../components/PlatformTabs';

function roleLabel(u: PlatformUserListItem): string {
  if (u.isPlatformAdmin) return 'Plattformadmin';
  if (u.organizations.some((o) => o.roleKey === 'club_admin')) return 'Vereinsadmin';
  return 'normaler Benutzer';
}

/** Elevated roles get the accent pill, "normaler Benutzer" the neutral one —
 * a restrained, single-color signal (no per-role color coding) that still
 * helps a platform admin spot elevated accounts at a glance on the compact
 * mobile list below. */
function isElevatedRole(u: PlatformUserListItem): boolean {
  return u.isPlatformAdmin || u.organizations.some((o) => o.roleKey === 'club_admin');
}

export function PlatformUsersPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { refresh, me } = useAuth();
  const { data } = useQuery({
    queryKey: ['platform', 'users'],
    queryFn: () => api<PlatformUserListItem[]>('/platform/users'),
  });

  const [impersonateError, setImpersonateError] = useState<string | null>(null);
  const impersonate = useMutation({
    mutationFn: (userId: string) => api(`/platform/users/${userId}/impersonate`, { method: 'POST' }),
    onSuccess: async () => {
      setImpersonateError(null);
      await refresh();
      await navigate({ to: '/' });
    },
    onError: (e) => setImpersonateError(e instanceof ApiError ? e.message : 'Anzeigen als fehlgeschlagen.'),
  });

  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const deleteUser = useMutation({
    mutationFn: (userId: string) => api(`/platform/users/${userId}`, { method: 'DELETE' }),
    onMutate: (userId) => setDeletingId(userId),
    onSettled: () => setDeletingId(null),
    onSuccess: () => {
      setDeleteError(null);
      void qc.invalidateQueries({ queryKey: ['platform', 'users'] });
    },
    onError: (e) => setDeleteError(e instanceof ApiError ? e.message : 'Löschen fehlgeschlagen.'),
  });

  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const rows = (data ?? []).filter((u) => {
    if (!needle) return true;
    const haystack = [u.firstName, u.lastName, u.email, ...u.organizations.map((o) => o.organizationName)]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(needle);
  });

  return (
    <div className="space-y-4">
      <PlatformTabs />
      <h1 className="hidden text-xl font-semibold md:block">Benutzeraccounts</h1>

      {impersonateError && <Alert>{impersonateError}</Alert>}
      {deleteError && <Alert>{deleteError}</Alert>}

      <Card>
        <Input
          placeholder="Suche (Vorname, Nachname, E-Mail, Verein)"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="mb-4 max-w-sm"
        />
        {/* Desktop: klassische Tabelle. Mobile (`md:hidden` unten): eine
         * Zeilen-Liste statt der Tabelle — eine horizontal scrollende
         * 820px-Tabelle auf ~390px Bildschirmbreite zeigte praktisch nur die
         * ersten beiden Spalten, der Rest (insbesondere die namensgebende
         * E-Mail-Spalte) lief ohne jeden Scroll-Hinweis über den Rand hinaus
         * (§ Nutzeranfrage). Die Liste setzt stattdessen alles untereinander:
         * Name (oder E-Mail, falls kein verknüpfter Mitgliedsdatensatz
         * existiert) fett als erste Zeile, E-Mail/Verein darunter in
         * abgestufter, kleinerer Schrift — eine schlichte, an Fließtext
         * orientierte Hierarchie statt Spalten/Karten-Rahmen, siehe
         * docs/administration.md. */}
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[820px] text-left text-sm">
            <thead className="text-slate-500">
              <tr className="border-b border-slate-200">
                <th className="py-2 pr-4 font-medium">Vorname</th>
                <th className="py-2 pr-4 font-medium">Nachname</th>
                <th className="py-2 pr-4 font-medium">E-Mail</th>
                <th className="py-2 pr-4 font-medium">Verein</th>
                <th className="py-2 pr-4 font-medium">Funktion</th>
                <th className="py-2 pr-4 font-medium">Aktion</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((u) => (
                <tr key={u.userId}>
                  <td className="py-2 pr-4">{u.firstName ?? '—'}</td>
                  <td className="py-2 pr-4">{u.lastName ?? '—'}</td>
                  <td className="py-2 pr-4">{u.email}</td>
                  <td className="py-2 pr-4">
                    {u.organizations.length > 0
                      ? u.organizations.map((o) => o.organizationName).join(', ')
                      : '—'}
                  </td>
                  <td className="py-2 pr-4">{roleLabel(u)}</td>
                  <td className="py-2 pr-4">
                    <div className="flex items-center gap-3">
                      {/* Nie für Plattformadmins/deaktivierte Konten — siehe
                       * AuthService.startImpersonation()'s serverseitige
                       * Prüfung, dieselben zwei Regeln, nur schon hier
                       * ausgeblendet statt erst nach einem Fehler. */}
                      {!u.isPlatformAdmin && u.status !== 'disabled' && (
                        <button
                          type="button"
                          onClick={() => {
                            setImpersonateError(null);
                            impersonate.mutate(u.userId);
                          }}
                          disabled={impersonate.isPending}
                          className="text-xs font-medium text-brand-accent hover:underline disabled:opacity-50"
                        >
                          Anzeigen als
                        </button>
                      )}
                      {/* Nie für Plattformadmins (dafür erst die
                       * Plattformadmin-Rolle unter /platform/admins entziehen)
                       * und nie für den eigenen Account — beides lehnt der
                       * Server ohnehin ab, hier schon ausgeblendet. */}
                      {!u.isPlatformAdmin && u.userId !== me?.user.id && (
                        <Button
                          type="button"
                          variant="danger"
                          className="px-2 py-1 text-xs"
                          disabled={deletingId === u.userId}
                          onClick={() => {
                            setDeleteError(null);
                            const orgList =
                              u.organizations.length > 0
                                ? ` und die Mitgliedsdatensätze in ${u.organizations.map((o) => o.organizationName).join(', ')}`
                                : '';
                            if (
                              confirm(
                                `„${u.email}" komplett aus der App entfernen? Das Benutzerkonto${orgList} werden unwiderruflich gelöscht.`,
                              )
                            ) {
                              deleteUser.mutate(u.userId);
                            }
                          }}
                        >
                          Entfernen
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="divide-y divide-slate-100 md:hidden">
          {rows.map((u) => {
            const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
            return (
              <div key={u.userId} className="flex items-center justify-between gap-3 py-1.5">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="truncate text-sm font-semibold text-slate-800">{fullName || u.email}</span>
                    <span
                      className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                        isElevatedRole(u) ? 'bg-brand-accent/10 text-brand-accent' : 'bg-slate-100 text-slate-500'
                      }`}
                    >
                      {roleLabel(u)}
                    </span>
                  </div>
                  {fullName && <div className="mt-0.5 truncate text-xs text-slate-500">{u.email}</div>}
                  <div className="mt-0.5 truncate text-xs text-slate-400">
                    {u.organizations.length > 0
                      ? u.organizations.map((o) => o.organizationName).join(', ')
                      : 'kein Verein'}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {!u.isPlatformAdmin && u.status !== 'disabled' && (
                    <button
                      type="button"
                      title="Anzeigen als"
                      aria-label="Anzeigen als"
                      onClick={() => {
                        setImpersonateError(null);
                        impersonate.mutate(u.userId);
                      }}
                      disabled={impersonate.isPending}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-brand-accent disabled:opacity-40"
                    >
                      <Eye size={16} />
                    </button>
                  )}
                  {!u.isPlatformAdmin && u.userId !== me?.user.id && (
                    <button
                      type="button"
                      title="Entfernen"
                      aria-label="Entfernen"
                      disabled={deletingId === u.userId}
                      onClick={() => {
                        setDeleteError(null);
                        const orgList =
                          u.organizations.length > 0
                            ? ` und die Mitgliedsdatensätze in ${u.organizations.map((o) => o.organizationName).join(', ')}`
                            : '';
                        if (
                          confirm(
                            `„${u.email}" komplett aus der App entfernen? Das Benutzerkonto${orgList} werden unwiderruflich gelöscht.`,
                          )
                        ) {
                          deleteUser.mutate(u.userId);
                        }
                      }}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {rows.length === 0 && <p className="py-4 text-sm text-slate-500">Keine Treffer.</p>}
        <p className="mt-3 text-xs text-slate-400">{rows.length} von {data?.length ?? 0} Benutzeraccounts</p>
      </Card>
    </div>
  );
}
